import { supabase } from '@/integrations/supabase/client';

export interface ConsumerNotification {
  id: string;
  title: string;
  message: string;
  type: 'quote' | 'reminder' | 'system' | 'message' | 'rating';
  timestamp: number;
  isRead: boolean;
  actionUrl?: string;
  actionLabel?: string;
  relatedId?: string;
  relatedType?: string;
}

/**
 * Fetch consumer notifications for the current user
 */
export const fetchConsumerNotifications = async (): Promise<ConsumerNotification[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log('No authenticated user');
      return [];
    }

    const { data, error } = await supabase
      .from('consumer_notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching consumer notifications:', error);
      return [];
    }

    return (data || []).map(notification => ({
      id: notification.id,
      title: notification.title,
      message: notification.message,
      type: notification.type as ConsumerNotification['type'],
      timestamp: new Date(notification.created_at).getTime(),
      isRead: notification.is_read,
      actionUrl: notification.action_url || undefined,
      actionLabel: notification.action_label || undefined,
      relatedId: notification.related_id || undefined,
      relatedType: notification.related_type || undefined,
    }));
  } catch (error) {
    console.error('Error in fetchConsumerNotifications:', error);
    return [];
  }
};

/**
 * Create a new consumer notification
 */
export const createConsumerNotification = async (
  notification: Omit<ConsumerNotification, 'id' | 'timestamp' | 'isRead'>
): Promise<ConsumerNotification | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('No authenticated user to create notification for');
      return null;
    }

    const { data, error } = await supabase
      .from('consumer_notifications')
      .insert({
        user_id: user.id,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        action_url: notification.actionUrl,
        action_label: notification.actionLabel,
        related_id: notification.relatedId,
        related_type: notification.relatedType,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating consumer notification:', error);
      return null;
    }

    return {
      id: data.id,
      title: data.title,
      message: data.message,
      type: data.type as ConsumerNotification['type'],
      timestamp: new Date(data.created_at).getTime(),
      isRead: data.is_read,
      actionUrl: data.action_url || undefined,
      actionLabel: data.action_label || undefined,
      relatedId: data.related_id || undefined,
      relatedType: data.related_type || undefined,
    };
  } catch (error) {
    console.error('Error in createConsumerNotification:', error);
    return null;
  }
};

/**
 * Mark a notification as read
 */
export const markConsumerNotificationAsRead = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('consumer_notifications')
      .update({ is_read: true })
      .eq('id', id);

    if (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in markConsumerNotificationAsRead:', error);
    return false;
  }
};

/**
 * Mark all notifications as read
 */
export const markAllConsumerNotificationsAsRead = async (): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { error } = await supabase
      .from('consumer_notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);

    if (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in markAllConsumerNotificationsAsRead:', error);
    return false;
  }
};

/**
 * Delete a notification
 */
export const deleteConsumerNotification = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('consumer_notifications')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting notification:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteConsumerNotification:', error);
    return false;
  }
};

/**
 * Create notification for accepted quote
 */
export const createQuoteAcceptedNotification = async (
  professionalName: string,
  requestDescription: string,
  requestId: string
): Promise<boolean> => {
  try {
    const notification = await createConsumerNotification({
      type: 'quote',
      title: 'הצעת מחיר אושרה!',
      message: `אישרת את ההצעה של ${professionalName} עבור "${requestDescription}"`,
      actionUrl: `/dashboard?tab=quotes&requestId=${requestId}`,
      actionLabel: 'צפה בבקשה',
      relatedId: requestId,
      relatedType: 'request',
    });

    return notification !== null;
  } catch (error) {
    console.error('Error creating quote accepted notification:', error);
    return false;
  }
};

/**
 * Create rating reminder notification
 */
export const createRatingReminderNotification = async (
  professionalName: string,
  requestId: string
): Promise<boolean> => {
  try {
    const notification = await createConsumerNotification({
      type: 'reminder',
      title: 'זכור לדרג את בעל המקצוע',
      message: `אנא דרג את ${professionalName} לאחר השירות`,
      actionUrl: `/dashboard?tab=quotes&action=rate&requestId=${requestId}`,
      actionLabel: 'דרג כעת',
      relatedId: requestId,
      relatedType: 'rating',
    });

    return notification !== null;
  } catch (error) {
    console.error('Error creating rating reminder notification:', error);
    return false;
  }
};
